import { useEffect } from 'react';
import { axios, type MutationFetcher, type SWRConfiguration, SWRKey, useSWR, useSWRMutation } from 'services/swr';
import { useUser } from 'services/User';

// Tenant-scoped CSWG proxy control plane.
//
// These hooks talk to the commercial-site backend (GATSBY_SITE_URL), which in
// turn proxies to the CSWG proxy (v0.8.x) slug-less, tenant-scoped endpoints
// under `/api/v1/tenant/<resource>`. The target tenant is resolved server-side
// from the authenticated session (email -> slug); the browser never sends a
// tenant slug. This repo is therefore tenant-admin only — there is no
// cross-tenant / platform-admin surface here.

// Wire casing matches the proxy (lowercase). The UI cards use their own
// casing and the orchestrator converts at the boundary.
export type CSWGProxyApiMode = 'passthrough' | 'enforce';
export type CSWGTenantStatusValue = 'active' | 'suspended';
export type CSWGTenantMetricsWindow = '15m' | '1h' | '24h' | '7d';

export interface CSWGDownloadOverrides {
    mode?: CSWGProxyApiMode;
    tls_mode?: string;
    min_file_size_kb?: number;
    max_file_size_kb?: number;
    bypass_domains?: ReadonlyArray<string>;
    fail_open?: boolean;
}

export interface CSWGUploadOverrides {
    enabled?: boolean;
    mode?: CSWGProxyApiMode;
    methods?: ReadonlyArray<string>;
    min_file_size_kb?: number;
    max_file_size_kb?: number;
    fail_open?: boolean;
}

export interface CSWGCdrOverrides {
    enabled?: boolean;
    rule?: string;
    block_if_clean_but_failed?: boolean;
    block_infected_even_if_sanitized?: boolean;
}

// Mirrors the proxy's accepted `config_overrides` keys (see control_plane
// `validate_config_override_keys`). `md_cloud_api_key` is write-only: it is
// never returned by GET and the UI injects the account API key on save.
export interface CSWGConfigOverrides {
    download?: CSWGDownloadOverrides;
    upload?: CSWGUploadOverrides;
    cdr?: CSWGCdrOverrides;
    siem_webhook_url?: string;
    md_cloud_api_endpoint?: string;
    md_cloud_request_timeout_secs?: number;
    md_cloud_api_key?: string;
}

export interface CSWGTenantConfigResponse {
    slug: string;
    name: string;
    status: CSWGTenantStatusValue;
    tenant_admins?: ReadonlyArray<string>;
    allowed_source_cidrs: ReadonlyArray<string>;
    config_overrides: CSWGConfigOverrides;
    retrieved_by: string;
}

// PUT body. The proxy uses `deny_unknown_fields`, so only these top-level keys
// may be sent. All are optional (sparse update).
export interface CSWGTenantConfigUpdateRequest {
    name?: string;
    status?: CSWGTenantStatusValue;
    tenant_admins?: ReadonlyArray<string>;
    allowed_source_cidrs?: ReadonlyArray<string>;
    config_overrides?: CSWGConfigOverrides;
}

export interface CSWGTenantConfigUpdateResponse {
    updated: boolean;
    slug: string;
    updated_by: string;
}

export interface CSWGTenantMetrics {
    tenant: string | null;
    window: string;
    total_requests: number;
    files_inspected: number;
    blocked: number;
    passed_through: number;
    cache_hit_rate_percent: number;
    data_processed_bytes: number;
    data_processed_human: string;
    avg_hold_latency_ms: number | null;
    avg_hold_latency: string;
    latency_p50_ms: number;
    latency_p95_ms: number;
    latency_p99_ms: number;
    active_connections: number;
}

export interface CSWGTenantLogsResponse {
    tenant: string;
    window: string;
    row_count: number;
    rows: ReadonlyArray<Record<string, string>>;
}

const CONFIG_URL = '/api/cswg-proxy/tenants/config';
const METRICS_URL = '/api/cswg-proxy/tenants/metrics';
const LOGS_URL = '/api/cswg-proxy/tenants/logs';

// Config is fetched once and is NOT auto-refetched after an error: no retry, no
// revalidate on focus/reconnect/stale. A fresh attempt happens only on an explicit
// refresh — a page reload, or the page's Refresh button (`mutate`). This stops the
// endpoint being hit on every navigation/focus when access is denied.
const CONFIG_SWR_DEFAULTS: SWRConfiguration<CSWGTenantConfigResponse> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
};

export const useCswgTenantConfig = (options?: SWRConfiguration<CSWGTenantConfigResponse>, enabled = true) => {
    // `enabled` gates the request (used by the access probe so we don't call the
    // backend before there is a session); the page leaves it on.
    const key: SWRKey | null = enabled ? { url: CONFIG_URL, baseURL: process.env.GATSBY_SITE_URL } : null;
    return useSWR<CSWGTenantConfigResponse>(key, { ...CONFIG_SWR_DEFAULTS, ...options });
};

export const useUpdateCswgTenantConfig = () => {
    const key: SWRKey = { url: CONFIG_URL, baseURL: process.env.GATSBY_SITE_URL };

    const fetcher: MutationFetcher<CSWGTenantConfigUpdateResponse, SWRKey, CSWGTenantConfigUpdateRequest> = async (
        { url, ...config },
        { arg },
    ) => {
        const res = await axios.put(url, arg, config);
        return res.data;
    };

    return useSWRMutation(key, fetcher, { revalidate: false });
};

export const useCswgTenantMetrics = (
    window: CSWGTenantMetricsWindow = '24h',
    options?: SWRConfiguration<CSWGTenantMetrics>,
) => {
    const key: SWRKey = {
        url: METRICS_URL,
        baseURL: process.env.GATSBY_SITE_URL,
        params: { window },
    };
    return useSWR<CSWGTenantMetrics>(key, options);
};

export const useCswgTenantLogs = (
    window: CSWGTenantMetricsWindow = '24h',
    options?: SWRConfiguration<CSWGTenantLogsResponse>,
) => {
    const key: SWRKey = {
        url: LOGS_URL,
        baseURL: process.env.GATSBY_SITE_URL,
        params: { window },
    };
    return useSWR<CSWGTenantLogsResponse>(key, options);
};

/**
 * Decides CSWG proxy tab visibility by probing the tenant config endpoint:
 * a successful GET means the session is a tenant admin (show the tab), an error
 * means it is not (hide it). The verdict is stored on the shared user context so
 * `canAccessCswg` (sidebar tab + `/cswg` route guard) can gate synchronously.
 *
 * Runs only for an authenticated session and shares the SWR cache key with the
 * page's `useCswgTenantConfig`, so the config is already loaded when the tab is
 * opened. This is an authorization probe — it never toasts.
 */
export const useCswgProxyAccess = () => {
    const { user, setUser } = useUser();
    const isLoggedIn = user?.isLoggedIn === true;

    // Probe only until the verdict is known. Once admin/non-admin is recorded we
    // stop calling GET config; a new probe happens only on a real refresh (a page
    // reload resets this context), not on every navigation while access is denied.
    const verdictKnown = user?.isCswgTenantAdmin !== undefined;
    const { data, error } = useCswgTenantConfig(
        {
            shouldRetryOnError: false,
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
            revalidateIfStale: true,
        },
        isLoggedIn && !verdictKnown,
    );
    const errorStatus = error?.response?.status;

    useEffect(() => {
        if (!isLoggedIn) return;

        if (data) {
            setUser((prev) => (prev?.isCswgTenantAdmin === true ? prev : { ...prev, isCswgTenantAdmin: true }));
        } else if (errorStatus === 401 || errorStatus === 403) {
            setUser((prev) => (prev?.isCswgTenantAdmin === false ? prev : { ...prev, isCswgTenantAdmin: false }));
        }
    }, [isLoggedIn, data, errorStatus, setUser]);
};
